import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/shared";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { EvaOsMark } from "@/components/landing/EvaOsMark";

const NEXT_KEY = "eva_auth_next";

const cream = "#F8F4EC";
const ink = "#1a1a1a";
const muted = "#6b6357";
const gold = "#a08c6e";

export default function Auth() {
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const next = params.get("next") || "/ambassadors/dashboard";
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();

  const isReturning = next === "/ambassadors/dashboard";

  useEffect(() => {
    if (!loading && user) {
      const stashed = sessionStorage.getItem(NEXT_KEY);
      const dest = stashed || next;
      sessionStorage.removeItem(NEXT_KEY);
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate, next]);

  const onGoogle = async () => {
    setBusy(true);
    try {
      sessionStorage.setItem(NEXT_KEY, next);
    } catch {
      /* sessionStorage may be unavailable; fallback to query param flow */
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth`,
      },
    });
    if (error) {
      toast({
        title: "Google sign-in error",
        description: error.message ?? "Something went wrong",
        variant: "destructive",
      });
      setBusy(false);
    }
    // On success the browser is redirecting — nothing more to do.
  };

  return (
    <div style={{ minHeight: "100vh", background: cream, color: ink, fontFamily: "Barlow, sans-serif", lineHeight: 1.6 }}>
      <header style={{ borderBottom: "1px solid rgba(0,0,0,0.08)", padding: "20px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}><span><EvaOsMark /></span></Link>
          <Link to="/" style={{ fontSize: 13, color: muted, textDecoration: "none" }}>← Back home</Link>
        </div>
      </header>
      <main style={{ maxWidth: 460, margin: "0 auto", padding: "80px 24px 120px" }}>
        <p style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, marginBottom: 12 }}>
          Eva Insiders
        </p>
        <h1 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1, margin: "0 0 16px", fontWeight: 400 }}>
          {isReturning ? "Welcome back." : "Join the Circle."}
        </h1>
        <p style={{ color: muted, marginBottom: 32 }}>
          {isReturning
            ? "One click and you're in your dashboard."
            : "Almost in. Google sign-in is how I keep this clean — no passwords, no spam, no shady email lists."}
        </p>

        <button
          onClick={onGoogle}
          disabled={busy}
          style={{
            width: "100%",
            padding: "14px 16px",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 10,
            background: "#fff",
            color: ink,
            fontSize: 15,
            fontWeight: 500,
            cursor: busy ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            opacity: busy ? 0.6 : 1,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          {busy ? "Working…" : "Continue with Google"}
        </button>

        {!isReturning && (
          <ol style={{ marginTop: 28, paddingLeft: 0, listStyle: "none", display: "grid", gap: 10, color: muted, fontSize: 14 }}>
            <li style={{ display: "flex", gap: 10 }}>
              <span style={{ color: gold, fontWeight: 600, minWidth: 16 }}>1.</span>
              <span>Approval inside 48hrs — the founder reads every application.</span>
            </li>
            <li style={{ display: "flex", gap: 10 }}>
              <span style={{ color: gold, fontWeight: 600, minWidth: 16 }}>2.</span>
              <span>Stripe Connect onboarding — your bank, paid the 1st of every month.</span>
            </li>
            <li style={{ display: "flex", gap: 10 }}>
              <span style={{ color: gold, fontWeight: 600, minWidth: 16 }}>3.</span>
              <span>Your share link goes live — first dollar typically lands inside the month.</span>
            </li>
          </ol>
        )}

        {!isReturning && (
          <p style={{ marginTop: 24, padding: "10px 14px", border: `1px solid ${gold}`, borderRadius: 10, background: "rgba(160,140,110,0.08)", color: ink, fontSize: 13, textAlign: "center", fontStyle: "italic" }}>
            You're applying as one of the first 50 Founding Insiders.
          </p>
        )}

        <p style={{ marginTop: 24, fontSize: 13, color: muted, textAlign: "center" }}>
          By continuing, you agree to the <Link to="/ambassadors/terms" style={{ color: ink }}>Ambassador Terms</Link> and our <Link to="/terms" style={{ color: ink }}>Terms of Service</Link>.
        </p>
      </main>
    </div>
  );
}
